const {
  formatAlternateIdKey,
  LOW_BALANCE_WARNING,
  ZERO_OR_NEGATIVE_BALANCE_THRESHOLD
} = require('@smarttransit/common')

const EXPIRED_TICKET_MANIFEST_GRACE_MSECS = 5 * 60 * 1000

// Coarse fetch bounds for prepaid trips on the manifest (datePrepaid lookback + expired grace).
// Admission below still decides what is shown.
const PREPAID_TICKET_RETRIEVAL_LOOKBACK_MSECS = 3 * 60 * 60 * 1000
const PREPAID_EXPIRED_TICKET_RETRIEVAL_GRACE_MSECS = 30 * 60 * 1000

const PASSENGER_LIST_SORT_PRIORITY = {
  UNRESOLVED_SCAN: 0,
  ZERO_OR_NEGATIVE_BALANCE: 1,
  LOW_BALANCE: 2,
  NORMAL: 3
}

function getTimestamp (value) {
  const timestamp = value ? new Date(value).getTime() : NaN
  return isNaN(timestamp) ? null : timestamp
}

function getTicketsForTrip (passengerTrip) {
  const logTrips = Array.isArray(passengerTrip && passengerTrip.passengerTransactionLogTrips)
    ? passengerTrip.passengerTransactionLogTrips
    : []
  return logTrips.map((o) => (o && o.passengerTransactionLog)).filter((o) => (o && o.id))
}

function getLatestTicket (tickets) {
  return [].concat(tickets || []).sort((a, b) => (
    new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
  ))[0]
}

function getValidTicketForTrip (passengerTrip) {
  const tickets = getTicketsForTrip(passengerTrip)

  // Only ever one ticket is valid against a trip; expired and transferred ones stay linked to it.
  // A guest is carried on a trip of its own, so extra tickets here are history, not extra riders.
  // With none valid we still show the newest within the grace window, which paints the row expired.
  return tickets.find((o) => (o.logStatus !== 'expired')) || getLatestTicket(tickets) || null
}

const TICKET_EXPIRED_STATUS = 'expired'

function isTimestampBeyondGrace (timestamp, now, graceMsecs) {
  if (timestamp === null) {
    return false
  }

  return timestamp + graceMsecs <= now
}

function resolveMetadataExpiresTimestamp (ticket) {
  if (!ticket || !ticket.logMetadata) {
    return null
  }

  return getTimestamp(ticket.logMetadata.expires)
}

function resolveExpiredStatusTimestamp (ticket) {
  if (!ticket || ticket.logStatus !== TICKET_EXPIRED_STATUS) {
    return null
  }

  return getTimestamp(ticket.dateUpdated) || getTimestamp(ticket.dateCreated)
}

function resolveExpiredTicketTimestamp (ticket) {
  return resolveMetadataExpiresTimestamp(ticket) || resolveExpiredStatusTimestamp(ticket)
}

function isTicketPastExpiryGrace (ticket, now, graceMsecs) {
  if (!ticket) {
    return false
  }

  return isTimestampBeyondGrace(resolveMetadataExpiresTimestamp(ticket), now, graceMsecs) ||
    isTimestampBeyondGrace(resolveExpiredStatusTimestamp(ticket), now, graceMsecs)
}

function isExpiredTicketBeyondManifestGrace (ticket, now = Date.now()) {
  return isTicketPastExpiryGrace(ticket, now, EXPIRED_TICKET_MANIFEST_GRACE_MSECS)
}

function hasExpiredTicketBeyondManifestGrace (passenger, now = Date.now()) {
  const passengerTrip = passenger && passenger.passengerTrip
  if (!passengerTrip) {
    return false
  }

  return isExpiredTicketBeyondManifestGrace(getValidTicketForTrip(passengerTrip), now)
}

function partitionPassengersByExpiredTicket (passengers, { now = Date.now() } = {}) {
  const validPassengers = []
  const expiredPassengers = []

  if (!Array.isArray(passengers)) {
    return { validPassengers, expiredPassengers }
  }

  passengers.forEach((passenger) => {
    if (hasExpiredTicketBeyondManifestGrace(passenger, now)) {
      expiredPassengers.push(passenger)
    } else {
      validPassengers.push(passenger)
    }
  })

  return { validPassengers, expiredPassengers }
}

function countManifestPassengerTickets (passengers, { now = Date.now() } = {}) {
  if (!Array.isArray(passengers)) {
    return 0
  }

  return partitionPassengersByExpiredTicket(passengers, { now })
    .validPassengers
    .filter((passenger) => passenger && passenger.userPayStatus).length
}

function resolvePassengerCountForGeolocation ({ passengerCount, passengers, now } = {}) {
  if (passengerCount !== null && passengerCount !== undefined && passengerCount !== '') {
    return passengerCount
  }

  return countManifestPassengerTickets(passengers, { now })
}

function resolvePrepaidTimestamp (passengerTrip) {
  return getTimestamp(passengerTrip && passengerTrip.datePrepaid ? passengerTrip.datePrepaid : null)
}

function isExpiredTicketBeyondRetrievalGrace (ticket, now = Date.now()) {
  return isTicketPastExpiryGrace(ticket, now, PREPAID_EXPIRED_TICKET_RETRIEVAL_GRACE_MSECS)
}

/**
 * Whether a prepaid trip is worth fetching onto the manifest before admission runs.
 * Uses datePrepaid within the lookback window, and keeps tickets that are still live or only
 * recently expired so the caller's tighter grace can decide.
 */
function isPrepaidTripEligibleForRetrieval (passengerTrip, { now = Date.now() } = {}) {
  if (!passengerTrip || !passengerTrip.datePrepaid) {
    return false
  }

  const prepaidTimestamp = resolvePrepaidTimestamp(passengerTrip)
  if (prepaidTimestamp === null || prepaidTimestamp < now - PREPAID_TICKET_RETRIEVAL_LOOKBACK_MSECS) {
    return false
  }

  const ticket = getValidTicketForTrip(passengerTrip)
  // No ticket relation loaded: keep the prepaid trip so admission can decide rather than drop a paid fare.
  if (!ticket) {
    return true
  }

  return !isExpiredTicketBeyondRetrievalGrace(ticket, now)
}

/**
 * Keep boarded trips untouched; prepaid-only rows must pass the retrieval window above.
 */
function pruneManifestTripsForRetrieval (passengerTrips, { boardedPassengerProfileIds, now = Date.now() } = {}) {
  if (!Array.isArray(passengerTrips)) {
    return []
  }

  const boardedIds = Array.isArray(boardedPassengerProfileIds) ? boardedPassengerProfileIds : []

  return passengerTrips.filter((passengerTrip) => {
    if (boardedIds.includes(passengerTrip.passengerProfileId)) {
      return true
    }

    return isPrepaidTripEligibleForRetrieval(passengerTrip, { now })
  })
}

function isPrepaidTripStillValid (passengerTrip, { now = Date.now() } = {}) {
  if (!passengerTrip.datePrepaid) {
    return false
  }

  // Hide once either logMetadata.expires or logStatus === 'expired' has passed the manifest grace.
  // Prepaid trips are created already `completed` and have no disembarked/transferred/audited
  // timestamp of their own, so ticket expiry is the retirement signal. A trip with no ticket
  // loaded is left alone rather than hidden, so a fare that was paid is never dropped over a
  // missing relation.
  const ticket = getValidTicketForTrip(passengerTrip)
  return !ticket || !isExpiredTicketBeyondManifestGrace(ticket, now)
}

function isCompletedNonPrepaidTrip (passengerTrip) {
  return !!(passengerTrip && passengerTrip.completed && !passengerTrip.datePrepaid)
}

function isTripAboardVehicle (passengerTrip, { transportationProfileId, boardedPassengerProfileIds, now = Date.now() }) {
  // A trip only belongs on this manifest when it names this vehicle outright. Trips carrying no
  // vehicle of their own are claimed by no bus, so they stay off every passenger list.
  if (!passengerTrip || passengerTrip.transportationProfileId !== transportationProfileId) {
    return false
  }

  if (isCompletedNonPrepaidTrip(passengerTrip)) {
    return false
  }

  const ticket = getValidTicketForTrip(passengerTrip)
  if (isExpiredTicketBeyondManifestGrace(ticket, now)) {
    return false
  }

  // Beyond that, either Map API reports the passenger aboard, or they hold a live prepaid trip,
  // which is shown ahead of boarding and so cannot be confirmed by presence.
  const boardedIds = Array.isArray(boardedPassengerProfileIds) ? boardedPassengerProfileIds : []
  return boardedIds.includes(passengerTrip.passengerProfileId) || isPrepaidTripStillValid(passengerTrip, { now })
}

function onlyTripsAboardVehicle (passengerTrips, { transportationProfileId, boardedPassengerProfileIds, now = Date.now() }) {
  if (!Array.isArray(passengerTrips)) {
    return []
  }

  const boardedIds = Array.isArray(boardedPassengerProfileIds) ? boardedPassengerProfileIds : []

  return passengerTrips.filter((o) => isTripAboardVehicle(o, {
    transportationProfileId,
    boardedPassengerProfileIds: boardedIds,
    now
  }))
}

function getBoardedPassengerProfileIds (passengerHash) {
  if (!Array.isArray(passengerHash) || !passengerHash.length) {
    return []
  }

  return [...new Set(passengerHash.map((o) => o.profileid).filter(Boolean))]
}

/**
 * Transport-app clears prepaid rows once the vehicle's last stop is past the trip destination.
 * Destination stop index < lastBusStopIndex means the prepaid trip is no longer live.
 */
function hasPrepaidTripPassedDestination (passengerTrip, { busStops, lastBusStopId } = {}) {
  if (!passengerTrip || !passengerTrip.datePrepaid) {
    return false
  }

  if (!lastBusStopId || !Array.isArray(busStops) || !busStops.length) {
    return false
  }

  const lastBusStopIndex = busStops.findIndex((o) => o && o.id === lastBusStopId)
  if (lastBusStopIndex < 0) {
    return false
  }

  const disembarked = passengerTrip.longlatDisembarked
  if (!disembarked || disembarked.lng === undefined || disembarked.lng === null ||
    disembarked.lat === undefined || disembarked.lat === null) {
    return false
  }

  const passengerFinalBusStopIndex = busStops.findIndex((o) => {
    return o && Array.isArray(o.longlat) &&
      o.longlat[0] === disembarked.lng &&
      o.longlat[1] === disembarked.lat
  })

  if (passengerFinalBusStopIndex < 0) {
    return false
  }

  return passengerFinalBusStopIndex < lastBusStopIndex
}

function filterTripsPastPrepaidDestination (passengerTrips, { busStops, lastBusStopId } = {}) {
  if (!Array.isArray(passengerTrips)) {
    return []
  }

  return passengerTrips.filter((passengerTrip) => {
    return !hasPrepaidTripPassedDestination(passengerTrip, { busStops, lastBusStopId })
  })
}

function isNoPassengerFoundBeaconUndetected ({ entry, normalizedKey, currentBeaconKeys, boardedTagKeys }) {
  if (!entry || !entry.noPassengerFound) {
    return false
  }

  // An explicit board (barcode / simulator) must keep the unresolved row even when lookup found
  // no passenger and /bluetooth/ has not yet listed the key. Dropping on currentBeacons alone
  // made unknown beacon IDs vanish as soon as the lookup returned.
  if (boardedTagKeys && boardedTagKeys.has(normalizedKey)) {
    return false
  }

  if (!currentBeaconKeys) {
    return false
  }

  return !currentBeaconKeys.has(normalizedKey)
}

function shouldDropPendingDetectedBeacon ({ entry, normalizedKey, boardedTagKeys, manifestBleKeys, currentBeaconKeys }) {
  if (!normalizedKey) {
    return true
  }

  if (manifestBleKeys && manifestBleKeys.has(normalizedKey)) {
    return true
  }

  if (isNoPassengerFoundBeaconUndetected({ entry, normalizedKey, currentBeaconKeys, boardedTagKeys })) {
    return true
  }

  if (boardedTagKeys && !boardedTagKeys.has(normalizedKey)) {
    return true
  }

  return false
}

function hasCachedBeaconsWithoutTrips (cached) {
  return Boolean(cached) && typeof cached === 'object' && !Array.isArray(cached) && Object.keys(cached).length > 0
}

function mergeBeaconsWithoutTripsHydration (cached, current) {
  const cachedEntries = hasCachedBeaconsWithoutTrips(cached) ? cached : {}
  const liveEntries = current && typeof current === 'object' && !Array.isArray(current) ? current : {}
  return Object.assign({}, cachedEntries, liveEntries)
}

function shouldThrottlePendingDetectedBeaconUpsert (existing, now, refreshMsec) {
  if (!existing || existing.noPassengerFound) {
    return false
  }

  const lastSeenAt = Number(existing.lastSeenAt || 0)
  if (!lastSeenAt) {
    return false
  }

  return (now - lastSeenAt) < refreshMsec
}

function reconcilePendingDetectedBeaconState (current, {
  boardedTagKeys,
  manifestBleKeys,
  currentBeaconKeys,
  now = Date.now()
} = {}) {
  const existing = current && typeof current === 'object' ? current : {}
  const next = {}
  let changed = false

  Object.keys(existing).forEach((key) => {
    const normalizedKey = formatAlternateIdKey(key)
    const entry = existing[key]

    if (shouldDropPendingDetectedBeacon({
      entry,
      normalizedKey,
      boardedTagKeys,
      manifestBleKeys,
      currentBeaconKeys
    })) {
      changed = true
      return
    }

    next[key] = { ...entry, lastSeenAt: now }
  })

  return changed
    ? { changed, beaconsWithoutTrips: next }
    : { changed, beaconsWithoutTrips: existing }
}

function isPassengerPaid (passengerTrip, { allowProgressiveWithoutError = true } = {}) {
  if (!passengerTrip) {
    return false
  }

  if (passengerTrip.datePrepaid || passengerTrip.datePaid) {
    return true
  }

  if (!allowProgressiveWithoutError) {
    return false
  }

  return !!(
    passengerTrip.metadata &&
    passengerTrip.metadata.isProgressive &&
    !passengerTrip.metadata.lastError
  )
}

function resolvePassengerProfileBalance (passengerOrTrip) {
  if (!passengerOrTrip) {
    return null
  }

  if (typeof passengerOrTrip.balance === 'number') {
    return passengerOrTrip.balance
  }

  const passengerTrip = passengerOrTrip.passengerTrip || passengerOrTrip
  const passengerProfile = passengerTrip && passengerTrip.passengerProfile
    ? passengerTrip.passengerProfile
    : null

  if (passengerProfile && typeof passengerProfile.balance === 'number') {
    return passengerProfile.balance
  }

  return null
}

function isUnresolvedPassengerScan (passengerRow) {
  return !!(passengerRow && passengerRow.unresolvedPassengerScan)
}

function resolvePassengerListSortTimestamp (passengerRow) {
  if (!passengerRow) {
    return 0
  }

  if (typeof passengerRow.sortTimestamp === 'number' && !isNaN(passengerRow.sortTimestamp)) {
    return passengerRow.sortTimestamp
  }

  if (isUnresolvedPassengerScan(passengerRow)) {
    return getTimestamp(passengerRow.lastSeenAt) ||
      getTimestamp(passengerRow.dateCreated) ||
      getTimestamp(passengerRow.timestamp) ||
      getTimestamp(passengerRow.dateAdded) ||
      0
  }

  const passengerTrip = passengerRow.passengerTrip || null

  if (passengerTrip && passengerTrip.datePrepaid) {
    return resolvePrepaidTimestamp(passengerTrip) || 0
  }

  if (passengerTrip) {
    const ticket = getValidTicketForTrip(passengerTrip)
    const ticketTimestamp = ticket ? getTimestamp(ticket.dateCreated) : null
    if (ticketTimestamp !== null) {
      return ticketTimestamp
    }

    return getTimestamp(passengerTrip.dateCreated) || 0
  }

  return getTimestamp(passengerRow.lastSeenAt) ||
    getTimestamp(passengerRow.dateCreated) ||
    getTimestamp(passengerRow.timestamp) ||
    getTimestamp(passengerRow.dateAdded) ||
    0
}

function getPassengerBalancePriority (passengerRow) {
  if (isUnresolvedPassengerScan(passengerRow)) {
    return PASSENGER_LIST_SORT_PRIORITY.UNRESOLVED_SCAN
  }

  const balance = resolvePassengerProfileBalance(passengerRow)

  if (typeof balance === 'number' && balance <= ZERO_OR_NEGATIVE_BALANCE_THRESHOLD) {
    return PASSENGER_LIST_SORT_PRIORITY.ZERO_OR_NEGATIVE_BALANCE
  }

  if (typeof balance === 'number' && balance < LOW_BALANCE_WARNING) {
    return PASSENGER_LIST_SORT_PRIORITY.LOW_BALANCE
  }

  return PASSENGER_LIST_SORT_PRIORITY.NORMAL
}

function comparePassengerListRows (left, right) {
  return resolvePassengerListSortTimestamp(right) - resolvePassengerListSortTimestamp(left)
}

function sortPassengerListRows (passengerRows) {
  if (!Array.isArray(passengerRows) || passengerRows.length < 2) {
    return Array.isArray(passengerRows) ? passengerRows.slice() : []
  }

  const groupedRows = {
    [PASSENGER_LIST_SORT_PRIORITY.UNRESOLVED_SCAN]: [],
    [PASSENGER_LIST_SORT_PRIORITY.ZERO_OR_NEGATIVE_BALANCE]: [],
    [PASSENGER_LIST_SORT_PRIORITY.LOW_BALANCE]: [],
    [PASSENGER_LIST_SORT_PRIORITY.NORMAL]: []
  }

  passengerRows.forEach((passengerRow) => {
    groupedRows[getPassengerBalancePriority(passengerRow)].push(passengerRow)
  })

  return groupedRows[PASSENGER_LIST_SORT_PRIORITY.UNRESOLVED_SCAN].sort(comparePassengerListRows)
    .concat(groupedRows[PASSENGER_LIST_SORT_PRIORITY.ZERO_OR_NEGATIVE_BALANCE].sort(comparePassengerListRows))
    .concat(groupedRows[PASSENGER_LIST_SORT_PRIORITY.LOW_BALANCE].sort(comparePassengerListRows))
    .concat(groupedRows[PASSENGER_LIST_SORT_PRIORITY.NORMAL].sort(comparePassengerListRows))
}

function resolveLowBalanceFlags (passengerTrip, { userPayStatus } = {}) {
  const balance = resolvePassengerProfileBalance(passengerTrip)
  const paid = typeof userPayStatus === 'boolean'
    ? userPayStatus
    : isPassengerPaid(passengerTrip)
  const extremelyLowBalance = !!(
    passengerTrip &&
    passengerTrip.metadata &&
    passengerTrip.metadata.lastErrorCode === 'insufficient-funds'
  )
  const lowBalance = !paid && typeof balance === 'number' && balance <= LOW_BALANCE_WARNING

  return {
    balance,
    lowBalance,
    extremelyLowBalance
  }
}

module.exports = {
  EXPIRED_TICKET_MANIFEST_GRACE_MSECS,
  PREPAID_EXPIRED_TICKET_RETRIEVAL_GRACE_MSECS,
  PREPAID_TICKET_RETRIEVAL_LOOKBACK_MSECS,
  PASSENGER_LIST_SORT_PRIORITY,
  countManifestPassengerTickets,
  filterTripsPastPrepaidDestination,
  getBoardedPassengerProfileIds,
  getPassengerBalancePriority,
  getValidTicketForTrip,
  hasCachedBeaconsWithoutTrips,
  hasExpiredTicketBeyondManifestGrace,
  hasPrepaidTripPassedDestination,
  isExpiredTicketBeyondManifestGrace,
  isTicketPastExpiryGrace,
  isPassengerPaid,
  isPrepaidTripEligibleForRetrieval,
  isPrepaidTripStillValid,
  isTripAboardVehicle,
  isUnresolvedPassengerScan,
  mergeBeaconsWithoutTripsHydration,
  onlyTripsAboardVehicle,
  partitionPassengersByExpiredTicket,
  pruneManifestTripsForRetrieval,
  reconcilePendingDetectedBeaconState,
  shouldThrottlePendingDetectedBeaconUpsert,
  resolveExpiredTicketTimestamp,
  resolveLowBalanceFlags,
  resolvePassengerCountForGeolocation,
  resolvePassengerListSortTimestamp,
  resolvePassengerProfileBalance,
  resolvePrepaidTimestamp,
  sortPassengerListRows
}
